import { TBaseItem } from "@centricsoftware/design-system";
import {
	ATTR_NAME,
	C8AttributeId, C8AttributeType, C8AttributeValue, C8Node, C8NodeType,
	CLASS_UOM_CONVERSION,
	cnl,
	DataStoreId, DUMMY,
	FORMAT_BARCODE,
	FORMAT_COMMENT,
	FORMAT_COMMENT_LATEST,
	FORMAT_DATE,
	FORMAT_RICH,
	FORMAT_URL,
	generateUnitOfMeasureURL, generateUOMTypeURL, getPrimitiveValueType, getUOMConversionString, getUOMNameAndUOMTypeNameFromConversionCNL,
	globalDataStoreId, isContainerValueType, isEnumValueType, isRefValueType, NODE_TYPE, Operator, relationDataStoreId, VALUE_TYPE
} from "c8-platform";
import JsonResponse from "csi/comm/JsonResponse";
import { createSelectOptionByType } from "csi/components/select/selectUtility";
import DataStoreNamesStore from "csi/components/select/store/DataStoreNamesStore";
import DataStoreNodesStore from "csi/components/select/store/DataStoreNodesStore";
import { focusOnFirstInputField, focusOnFirstInvalidInputField } from "csi/middleware/form/formUtility";
import { PreferenceView } from "csi/prefs/PreferenceView";
import { getClass, getClasses, getNode } from "csi/reflection/reflection";
import {
	ATTR_AUTOFILL_DEPENDENT_ENUM, ATTR_COLUMN_WIDTH, ATTR_COMMENT_RESTRICTED, ATTR_COPY_CATEGORIES, ATTR_COPY_CATEGORIES_LOCKED, ATTR_COPY_OPTION_NAME,
	ATTR_DEPENDS_ON, ATTR_DEPENDS_ON_PATH, ATTR_ENABLED, ATTR_EXP, ATTR_EXP_ONCLIENT_DISABLED, ATTR_EXPRESSION_STATE, ATTR_FORMAT, ATTR_HIDDEN, ATTR_NOTES,
	ATTR_OPTIONAL, ATTR_REFLECTION_ATTR_OWNER, ATTR_REQUIRED, ATTR_REST_EXPOSED, ATTR_REST_RESTRICTED, ATTR_REST_UPDATE, ATTR_SETUP_SETTINGS, ATTR_SUBTYPE,
	ATTR_TARGET_CLASS_NAME, ATTR_TARGET_CLASS_SUBTYPE_ID, ATTR_VALIDATION_EXPRESSION, ATTR_VALUE, ATTR_VALUETYPE, COPY_CATEGORY_ENUM, COPY_CATEGORY_NO_CONTRIBUTE,
	COPY_CATEGORY_NO_COPY, COPY_CATEGORY_OPTIONAL, COPY_CATEGORY_OPTIONAL_PASSIVE, PATH_PARENT, TYPE_BUSINESS_OBJECT, TYPE_CONFIGURABLE_ATTRIBUTE, TYPE_ENUM,
	TYPE_IMAGE, TYPE_LOOKUP_ITEM, TYPE_TAG
} from "csi/reflection/reflectionConstants";
import { resolveNode, resolveNodesWithDependencies } from "csi/service/nodeService";
import { submitWriter } from "csi/service/writerService";
import siteDefaultLocale from "csi/siteDefaultLocale";
import { SwitchboardWidget } from "csi/table/SwitchboardWidget";
import types from "csi/types";
import { ICache } from "csi/typings";
import { cloneObjectWithProxy, ReactionStore } from "csi/utility/mobxUtility";
import { getUrlPath, pathCreate } from "csi/utility/pathUtility";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import Search from "csi/xml/Search";
import Writer from "csi/xml/Writer";
import { ILocaleProvider } from "designSystem/locale";
import { clone, isEqual, omit, orderBy, sortBy } from "lodash";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { ChangeEventHandler, createContext, RefObject, SyntheticEvent, useContext } from "react";
import { C8RequestError } from "src/c8-platform/core/request";
import { generateUOMConversionURL, UOM_SUFFIX } from "src/c8-platform/type/uom";
import BusinessObject from "src/SiteAdmin/BusinessObject";
import Locale from "src/SiteAdmin/Locale";
import AttributeDefinitionLocale from "./AttributeDefinitionLocale";
import { createConfigurableAttributeSelectOption, getAttributeTargetClassAndSubtype, getDependsOnSubtypeEnumPath, getDependsOnSubtypeEnumUrl, isCustomAttribute } from "./attributeDefinitionService";
import { COLUMN_SIZE_LOCALIZED, TYPES } from "./AttributeDefinitionTypes";
import { getOwnerClassForBusinessObject, validateAttributeId, validateCopyOptions, validateInitialValue, validateRequiredStringValue, validateSelectEnumeration, validateValidationExpression, validateWithError } from "./AttributeDefinitionValidationService";
import ConfigurableAttributeSelectStore from "./ConfigurableAttributeSelectStore";
import ConfigurableEnumValueSelectStore from "./ConfigurableEnumValueSelectStore";

//TODO: make it works for date/time

interface RadioButtonOptionsType {
	id?: string;
	label: string;
	value: string;
}

const TARGET_CLASS_NAMES_WITH_SUBTYPE = {
	LookupItem: "LookupItemSubtype",
	SpecificationItemDefinition: "SpecificationDataSheetSubtype",
};

const TARGET_CLASS_AS_FORMAT = new Set([TYPE_IMAGE, TYPE_TAG]);

const EXPRESSION_RUNTIME_ENUM = "ExpressionState";
const EXPRESSION_RUNTIME_ENUM_EMPTY = xml.enumCreate(EXPRESSION_RUNTIME_ENUM, "");
const EXPRESSION_RUNTIME_ENUM_DISABLED = xml.enumCreate(EXPRESSION_RUNTIME_ENUM, "Disabled");
export const EXPRESSION_RUNTIME_ENUM_ONETIME = xml.enumCreate(EXPRESSION_RUNTIME_ENUM, "OneTime");
export const EXPRESSION_RUNTIME_ENUM_CONTINUOUS = xml.enumCreate(EXPRESSION_RUNTIME_ENUM, "Continuous");

const COPY_CATEGORY_ALL = "All";
const COPY_CATEGORY_ENUM_COPY = xml.enumCreate(COPY_CATEGORY_ENUM);
const COPY_CATEGORY_ENUM_NO_COPY = xml.enumCreate(COPY_CATEGORY_ENUM, COPY_CATEGORY_NO_COPY);
const COPY_CATEGORY_ENUM_OPTIONAL = xml.enumCreate(COPY_CATEGORY_ENUM, COPY_CATEGORY_OPTIONAL);
const COPY_CATEGORY_ENUM_NO_CONTRIBUTE = xml.enumCreate(COPY_CATEGORY_ENUM, COPY_CATEGORY_NO_CONTRIBUTE);
const COPY_CATEGORY_ENUM_OPTIONAL_PASSIVE = xml.enumCreate(COPY_CATEGORY_ENUM, COPY_CATEGORY_OPTIONAL_PASSIVE);

const REST_NONE = "none";
const REST_READ = "read";
const REST_WRITE = "write";

const UNIT_OF_MEASURE = "UnitOfMeasure";
const UNIT_OF_MEASURE_TYPE = "UOMType";

/** create an option for Copy Options select list options  */
function createRadioOption (id: string, label: string): RadioButtonOptionsType {
	const opt = {
		id,
		label,
		value: id,
	};
	return opt;
}

/** return copy categories object for runtime - if no rule for All, then add All: Copy */
function getCopyCategoriesForRuntime(copyCategories: Record<string, string>): Record<string, string> {
	const obj = clone(copyCategories);
	obj[COPY_CATEGORY_ALL] ||= COPY_CATEGORY_ENUM_COPY;
	return obj;
}

/** return copy categories object for saving - if All: Copy, then omit from storage */
function getCopyCategoriesToSave(copyCategories: Record<string, string>): Record<string, string> {
	const obj = cloneObjectWithProxy(copyCategories);

	if (obj[COPY_CATEGORY_ALL] === COPY_CATEGORY_ENUM_COPY) {
		return omit(obj, COPY_CATEGORY_ALL);
	}

	return obj as Record<string, string>;
}

/** return true if configuration was updated for this attribute */
function isConfigUpdatedAttribute(node: C8Node): boolean {
	return !!node?.AppliedOn;
}

export default class AttributeDefinitionStore extends ReactionStore {
	/** cache */
	readonly cache: ICache;

	/** locale provider */
	readonly loc: ILocaleProvider;
	
	/** locale resources */
	readonly locale: AttributeDefinitionLocale;
	
	/** Business Object containing, or that will contain, the attribute - could be different than parent in New from Inherited case */
	readonly nodeBO: C8Node;
	
	/** configurable attribute node in Edit case */
	readonly nodeCA?: C8Node;

	/** attribute nodes generated by action */
	resultNodes: C8Node[] = [];

	/** configurable attribute which is source node for New from Inherited action */
	readonly sourceNodeCA?: C8Node;

	// local utility data
	private editEnumerationView: SwitchboardWidget;
	private _enumListDataStore?: DataStoreNamesStore;
	private _uomListDataStore?: DataStoreNamesStore;
	private _uomTypeListDataStore?: DataStoreNamesStore;
	
	attrUOMName: string;
	attrUOMTypeName: string;
	
	@observable attrAutofillDependent: boolean;
	@observable attrCopyCategories: Record<string, string>;
	@observable attrCopyOptionsName: string;
	@observable attrCopyOptionsNameError: string;
	@observable attrDisableDragEdits: boolean;
	@observable attrEnableComments: boolean;
	@observable attrEnabled: boolean;
	@observable attrExpression: string;
	@observable attrExpressionError: string;
	@observable attrExpressionEvaluateOnClient: boolean;
	@observable attrFormat: string;
	@observable attrHidden: boolean;
	@observable attrId: C8AttributeId;
	@observable attrIdError: string;
	@observable attrInitialValue: C8AttributeValue;
	@observable attrInitialValueError: string;
	@observable attrName: string;
	@observable attrNotes: string;
	@observable attrRESTAccess: string;
	@observable attrReadonly: boolean;
	@observable attrRequired: boolean;
	@observable attrSelectEnumeration: string;
	@observable attrSelectEnumerationCnl?: cnl;
	@observable attrSelectEnumerationError: string;
	@observable attrSubtypeConfiguration: boolean;
	@observable attrTooltip: string;
	@observable attrType: C8AttributeType;
	@observable attrUOM: string;
	@observable attrUOMEnabled: boolean;
	@observable attrUOMError: string;
	@observable attrUOMType: string;
	@observable attrUOMTypeError: string;
	@observable attrValidationErrorMessage: string;
	@observable attrValidationExpression: string;
	@observable attrValidationExpressionError: string;
	@observable columnWidth: string;
	@observable.ref dependsOnAttribute?: TBaseItem;
	@observable dependsOnEnumList?: string;
	@observable dependsOnSubtypeUrl?: cnl;
	@observable expressionState: string;
	@observable formError: string;
	@observable openEnumerationsModal = false;
	@observable ownerClass: C8NodeType;
	@observable targetClass?: C8NodeType;
	@observable targetClassError?: string;
	@observable.ref targetClassSubtype?: TBaseItem;
	@observable targetClassSubtypeError?: string;
 
	/** form ref */
	formRef: RefObject<HTMLFormElement>;

	/** set form ref, focus the first required field on mount */
	setFormRef = (ref: RefObject<HTMLFormElement>): void => {
		this.formRef = ref;
		if (ref) {
			this.focusFirstInputField();
		}
	};

	/** focus the first required field of this form ref */
	focusFirstInputField = (): void => {
		focusOnFirstInputField(this.formRef.current);
	};

	/** focus on the first invalid field */
	focusFirstInvalidInputField = (): void => {
		focusOnFirstInvalidInputField(this.formRef.current);
	};

	/** return option for selected enum value */
	@computed get attrEnumInitialValue(): TBaseItem | undefined {
		const { attrInitialValue, attrSelectEnumeration, loc } = this;

		if (attrSelectEnumeration && (typeof attrInitialValue === "string") && (attrInitialValue !== xml.enumCreate(attrSelectEnumeration))) {
			return {
				id: attrInitialValue,
				name: loc.enumResource(attrInitialValue),
			};
		}
	}

	/** indicates if expression state indicates that the expression is enabled */
	get attrExpressionEnabled(): boolean {
		return (this.expressionState === EXPRESSION_RUNTIME_ENUM_CONTINUOUS) ||
			(this.expressionState === EXPRESSION_RUNTIME_ENUM_ONETIME);
	}

	/** indicates if column width is variable (not calculated) */
	get canSelectColumnWidth(): boolean {
		// column width is calculated automatically for images
		return !this.isRefType || (this.targetClass !== TYPE_IMAGE);
	}
	
	private get changedAutofillDependent(): boolean {
		return this.attrAutofillDependent !== this.nodeCA[ATTR_AUTOFILL_DEPENDENT_ENUM];
	}

	private get changedColumnWidth(): boolean {
		return this.columnWidth !== (xml.enumGetText(this.nodeCA[ATTR_COLUMN_WIDTH]) || this.columnWidthDefault);
	}

	@computed private get changedCopyCategories(): boolean {
		const { attrCopyCategories, nodeCA } = this;
		const obj = getCopyCategoriesToSave(attrCopyCategories);
		return !isEqual(obj, nodeCA[ATTR_COPY_CATEGORIES]);
	}

	private get changedCopyOptionsName(): boolean {
		return this.attrCopyOptionsName !== this.nodeCA[ATTR_COPY_OPTION_NAME];
	}

	private get changedDependsOnAttribute(): boolean {
		return (this.dependsOnAttribute?.id || DUMMY) !== this.nodeCA[ATTR_DEPENDS_ON];
	}

	private get changedDisableDragEdits(): boolean {
		return this.attrDisableDragEdits !== this.nodeCA[BusinessObject.ATTR_DRAG_EDIT_DISABLED];
	}

	private get changedEnableComments(): boolean {
		return this.attrEnableComments !== !this.nodeCA[ATTR_COMMENT_RESTRICTED];
	}

	private get changedEnabled(): boolean {
		return this.attrEnabled !== this.nodeCA[ATTR_ENABLED];
	}

	private get changedEnumeration(): boolean {
		return this.attrSelectEnumeration !== (this.isEnumType ? this.nodeCA[ATTR_FORMAT] : "");
	}

	private get changedExpression(): boolean {
		return this.attrExpression !== this.nodeCA[ATTR_EXP];
	}

	private get changedExpressionEvaluateOnClient(): boolean {
		return this.attrExpressionEvaluateOnClient !== !this.nodeCA[ATTR_EXP_ONCLIENT_DISABLED];
	}

	private get changedExpressionState(): boolean {
		const { expressionState, nodeCA, _getInitialExpressionState } = this;
		const existingState = _getInitialExpressionState(nodeCA);
		return expressionState !== existingState;
	}

	private get changedFormat(): boolean {
		return this.attrFormat !== this.nodeCA[ATTR_FORMAT];
	}

	private get changedHidden(): boolean {
		return this.attrHidden !== this.nodeCA[ATTR_HIDDEN];
	}

	private get changedInitialValue(): boolean {
		const initialValue = this._getInitialValueForSave();
		return initialValue !== this.nodeCA[ATTR_VALUE];
	}

	private get changedName(): boolean {
		return this.attrName !== Locale.getConfigurableAttributeNodeName(this.nodeCA);
	}

	private get changedNotes(): boolean {
		return this.attrNotes !== this.nodeCA[ATTR_NOTES];
	}

	private get changedOwnerClass(): boolean {
		return this.ownerClass !== Locale.getConfigurableAttributeType(this.nodeCA);
	}

	private get changedRESTAccess(): boolean {
		return this.attrRESTAccess !== (this.nodeCA[ATTR_REST_EXPOSED] ? (this.nodeCA[ATTR_REST_RESTRICTED] ? REST_READ: REST_WRITE) : REST_NONE);
	}
	
	private get changedReadonly(): boolean {
		return this.attrReadonly !== !!(this.nodeCA[BusinessObject.ATTR_ATTRIBUTE_FLAGS] & xml.ATTR_FLAG_NOEDIT);
	}

	private get changedRequired(): boolean {
		return this.attrRequired !== this.nodeCA[ATTR_REQUIRED];
	}

	private get changedSubtypeConfiguration(): boolean {
		return this.attrSubtypeConfiguration !== this.nodeCA[ATTR_OPTIONAL];
	}

	private get changedTargetClassAndSubtype(): boolean {
		const { nodeCA, targetClass, targetClassSubtype } = this;
		const [existingTargetClass, existingTargetClassSubtypeUrl] = getAttributeTargetClassAndSubtype(nodeCA);
		const targetClassSubtypeUrl = targetClassSubtype?.id || DUMMY;

		return (targetClass !== existingTargetClass) || (targetClassSubtypeUrl !== existingTargetClassSubtypeUrl);
	}

	private get changedTooltip(): boolean {
		const existingOwnerClass = Locale.getConfigurableAttributeType(this.nodeCA);
		return this.attrTooltip !== Locale.getTypeAttributeResource(existingOwnerClass, this.attrId, Locale.TOOLTIP);
	}

	private get changedType(): boolean {
		return this.attrType !== this.nodeCA[ATTR_VALUETYPE];
	}

	private get changedValidationErrorMessage(): boolean {
		const existingOwnerClass = Locale.getConfigurableAttributeType(this.nodeCA);
		return this.attrValidationErrorMessage !== Locale.getTypeAttributeResource(existingOwnerClass, this.attrId, Locale.ATTR_VALIDATION);
	}
	
	private get changedValidationExpression(): boolean {
		return this.attrValidationExpression !== this.nodeCA[ATTR_VALIDATION_EXPRESSION];
	}

	/** return default column size select value */
	@computed get columnWidthDefault(): string {
		const { attrId, attrType, attrFormat, canSelectColumnWidth } = this;

		if (canSelectColumnWidth) {
			return xml.enumGetText(types.getAttributeDefaultColumnWidth(attrId, attrType, attrFormat)) || types.SMALL;
		}

		return "";
	}

	/** return options for column width select */
	@computed({ keepAlive: true }) get columnWidthOptions(): TBaseItem[] {
		const { loc } = this;

		return Object.keys(types.COLUMN_WIDTHS).map((id) => {
			const name = loc.resource(COLUMN_SIZE_LOCALIZED[id]);
			return { id, name };
		});
	}

	/**
	 * return options for Copy Options select, add Optional-Passive option if value is set to Optional-Passive (C8-75982)
	 */
	getCopyOptionsOptions(value: string): RadioButtonOptionsType[] {
		const { copyOptionsOptions, loc } = this;
		const labelOptionalPassive = loc.enumResource(COPY_CATEGORY_ENUM_OPTIONAL_PASSIVE);
		let options = copyOptionsOptions;

		// rare case when OOTB attribute has value Optional-Passive
		// Note: we do not allow this value for newly created custom attributes
		// Note2: if user change value from Optional-Passive to other value and save, 
		//        he wont be able to restore Optional-Passive value (discussed with Dan Ash)
		if (value === COPY_CATEGORY_ENUM_OPTIONAL_PASSIVE) {
			options = [].concat(copyOptionsOptions);
			options.push(createRadioOption(COPY_CATEGORY_ENUM_OPTIONAL_PASSIVE, labelOptionalPassive));
		}
		return options;
	}

	/**
	 * return options for Copy Options select
	 */
	@computed get copyOptionsOptions(): RadioButtonOptionsType[] {
		const { isRefType, loc } = this;
		const labelCopy =  loc.resource("AttributeDefinitionCopyDefaultOption");
		const labelNoCopy = loc.enumResource(COPY_CATEGORY_ENUM_NO_COPY);
		const labelOptional = loc.enumResource(COPY_CATEGORY_ENUM_OPTIONAL);
		const labelNoContribute = loc.enumResource(COPY_CATEGORY_ENUM_NO_CONTRIBUTE);
		const options = [];
		options.push(createRadioOption(COPY_CATEGORY_ENUM_COPY, labelCopy));
		options.push(createRadioOption(COPY_CATEGORY_ENUM_NO_COPY, labelNoCopy));
		// According to Ron M. suggestion this is temporary disabled and made available for all BOs
		/*isStyleBO && */options.push(createRadioOption(COPY_CATEGORY_ENUM_OPTIONAL, labelOptional));
		isRefType && options.push(createRadioOption(COPY_CATEGORY_ENUM_NO_CONTRIBUTE, labelNoContribute));
		return options;
	}

	/** default format for selected attribute type */
	get defaultFormat(): string {
		return (getPrimitiveValueType(this.attrType) === VALUE_TYPE.TIME) ? FORMAT_DATE : "";
	}

	/** default value for initial value based on selected attribute type and format */
	get defaultInitialValue(): string {
		const defaultValue = xml.attributeTypeGetDefaultValue(this.attrType, this.attrFormat);
		return defaultValue;
	}

	/** path to class with subtype attribute on which this attribute depends */
	get dependsOnAttributePath(): string {
		const { dependsOnAttribute, dependsOnEnumList, ownerClass } = this;

		if (dependsOnAttribute && (dependsOnAttribute.id === getDependsOnSubtypeEnumUrl(ownerClass, dependsOnEnumList))) {
			return getDependsOnSubtypeEnumPath(ownerClass);
		}

		return "";
	}

	/** data store parameters for selecting an attribute that this attribute will depend on (for dependent enums and refs to LookupItem) */
	@computed get dependsOnDataStore(): DataStoreNodesStore | undefined {
		const { dependsOnEnumList, dependsOnSubtypeUrl, nodeBO, ownerClass } = this;
		const q = new Query();
		let dataStoreId: DataStoreId;
		let sourceUrl: cnl;
		
		if (dependsOnEnumList) {
			const subtypeAttributeUrl = getDependsOnSubtypeEnumUrl(ownerClass, dependsOnEnumList);
			if (subtypeAttributeUrl) {
				dataStoreId = globalDataStoreId(NODE_TYPE.BUILD_IN_ATTRIBUTE);
				q.Node(xml.PROP_URL, Operator.EQUALS, subtypeAttributeUrl);
			} else {
				dataStoreId = relationDataStoreId(TYPE_BUSINESS_OBJECT, "ConfigurableAttributes", TYPE_CONFIGURABLE_ATTRIBUTE);
				sourceUrl = nodeBO.$URL;
				q.Attr(ATTR_REFLECTION_ATTR_OWNER, Operator.EQUALS, ownerClass);
				q.Attr(ATTR_FORMAT, Operator.EQUALS, dependsOnEnumList);
				q.Attr(ATTR_VALUETYPE, Operator.EQUALS, VALUE_TYPE.ENUM);
			}
		} else if (dependsOnSubtypeUrl) {
			dataStoreId = relationDataStoreId(TYPE_BUSINESS_OBJECT, "ConfigurableAttributes", TYPE_CONFIGURABLE_ATTRIBUTE);
			sourceUrl = nodeBO.$URL;
			q.Attr(ATTR_REFLECTION_ATTR_OWNER, Operator.EQUALS, ownerClass);
			q.Attr(ATTR_TARGET_CLASS_NAME, Operator.EQUALS, TYPE_LOOKUP_ITEM);
			q.RefAttr(ATTR_SUBTYPE, Operator.EQUALS, dependsOnSubtypeUrl);
		}

		if (dataStoreId) {
			return new ConfigurableAttributeSelectStore({
				dataStoreId,
				queryXml: q.toString(),
				sourceUrl,
			});
		}
	}

	/** parameters for enumerations view */
	@computed get editEnumerationViewParams(): Record<string, any> {
		const { attrSelectEnumeration, attrSelectEnumerationCnl } = this;
		const query = attrSelectEnumeration ? { [ATTR_NAME]: [attrSelectEnumeration] } : {};
		return {
			bPopup: true,
			bUseLocation: false,
			filterRemember: false,
			onCreateDefaultCustomView: (cv: PreferenceView): void => {
				cv.removeCustomOption("filterRemember");
			},
			query,
			urlPreSelect: attrSelectEnumerationCnl,
		};
	}

	/** data store containing options for selecting an enumlist when attribute type is enum */
	get enumListDataStore(): DataStoreNamesStore {
		if (!this._enumListDataStore) {
			const dataStoreId = globalDataStoreId(TYPE_ENUM, "AvailableEnumLists");
			this._enumListDataStore = new DataStoreNamesStore({ dataStoreId });
		}

		return this._enumListDataStore;
	}

	/** options for selected enumlist (max length 1) */
	@computed get enumListValue(): TBaseItem | undefined {
		const { attrSelectEnumeration, attrSelectEnumerationCnl } = this;
		if (attrSelectEnumeration) {
			return {
				id: attrSelectEnumerationCnl,
				name: attrSelectEnumeration,
			};
		}
	}

	/** options for selecting an enumvalue when attribute type is enum */
	@computed get enumValueDataStore(): DataStoreNamesStore | undefined {
		const { attrSelectEnumeration, attrSelectEnumerationCnl } = this;

		if (attrSelectEnumerationCnl) {
			return new ConfigurableEnumValueSelectStore(attrSelectEnumeration, attrSelectEnumerationCnl);
		}
	}

	/** return options for format select */
	@computed get formatOptions(): TBaseItem[] {
		const { attrType } = this;
		const formats = types.getFormats(attrType);
		const data : TBaseItem[] = [];
	
		formats?.forEach((format) => {
			// skip empty format
			if (format) {
				data.push({
					id: format,
					name: types.getLocalizedFormat(format),
				});
			}
		});

		// sort formats unless numeric format
		const primitiveType = getPrimitiveValueType(attrType);
		if ((primitiveType === VALUE_TYPE.DOUBLE) || (primitiveType === VALUE_TYPE.INTEGER)) {
			return data;
		}

		return sortBy(data, "name");
	}

	/**
	 * return format current select value
	 */
	get formatOptionsValue(): TBaseItem | undefined {
		const { attrFormat, formatOptions } = this;
		if (attrFormat) {
			return formatOptions.find((item) => item.id === attrFormat);
		}
	}

	/** initial value field is visible */
	@computed get initialValueHidden(): boolean {
		const { attrFormat, attrSelectEnumerationCnl, attrType } = this;
		return isContainerValueType(attrType as C8AttributeType) ||
			(attrType === VALUE_TYPE.REF) ||
			(attrType === VALUE_TYPE.URL) ||
			(attrType === VALUE_TYPE.ENUM && !attrSelectEnumerationCnl) ||
			(attrType === VALUE_TYPE.STRING && (
				attrFormat === FORMAT_BARCODE ||
				attrFormat === FORMAT_COMMENT_LATEST ||
				attrFormat === FORMAT_COMMENT ||
				attrFormat === FORMAT_URL ||
				attrFormat === FORMAT_RICH
			));
	}

	/**
	 * return true is not editing or if some attribute was changed via editing
	 */
	get isAttrChanged(): boolean {
		// quick exit to prevent JS errors in case
		if (!this.nodeCA) {
			return true;
		}

		const {
			changedAutofillDependent,
			changedColumnWidth,
			changedCopyCategories,
			changedCopyOptionsName,
			changedDependsOnAttribute,
			changedDisableDragEdits,
			changedEnableComments,
			changedEnabled,
			changedEnumeration,
			changedExpression,
			changedExpressionEvaluateOnClient,
			changedExpressionState,
			changedFormat,
			changedHidden,
			changedInitialValue,
			changedName,
			changedNotes,
			changedOwnerClass,
			changedRESTAccess,
			changedReadonly,
			changedRequired,
			changedSubtypeConfiguration,
			changedTargetClassAndSubtype,
			changedTooltip,
			changedType,
			changedValidationErrorMessage,
			changedValidationExpression,
		} = this;

		return changedAutofillDependent ||
			changedColumnWidth ||
			changedCopyCategories ||
			changedCopyOptionsName ||
			changedDependsOnAttribute ||
			changedDisableDragEdits ||
			changedEnableComments ||
			changedEnabled ||
			changedEnumeration ||
			changedExpression ||
			changedExpressionEvaluateOnClient ||
			changedExpressionState ||
			changedFormat ||
			changedHidden ||
			changedInitialValue ||
			changedName ||
			changedNotes ||
			changedOwnerClass ||
			changedRESTAccess ||
			changedReadonly ||
			changedRequired ||
			changedSubtypeConfiguration ||
			changedTargetClassAndSubtype ||
			changedTooltip ||
			changedType ||
			changedValidationErrorMessage ||
			changedValidationExpression;
	}

	/** indicates if editing an existing attribute */
	get isEdit(): boolean {
		return !!this.nodeCA;
	}

	/** indicates if format is required for selected value type */
	get isFormatRequired(): boolean {
		return getPrimitiveValueType(this.attrType) === VALUE_TYPE.TIME;
	}

	/** indicates if creating a new custom attribute using New action */
	get isNew(): boolean {
		return !this.nodeCA && !this.sourceNodeCA;
	}

	/** indicates if creating an attribute using New from Inherited action */
	get isNewFromInherited(): boolean {
		return !!this.sourceNodeCA;
	}

	/** indicates if chosen value type is a ref and the target class is LookupItem */
	get isRefToSubtypedClass(): boolean {
		const { isRefType, targetClass } = this;
		return isRefType && !!TARGET_CLASS_NAMES_WITH_SUBTYPE[targetClass];
	}

	/** indicates if chosen value type is a ref */
	get isRefType(): boolean {
		return isRefValueType(this.attrType);
	}

	/** return true if attribute is build in */
	get isBuiltInAttribute(): boolean {
		const { nodeCA } = this;
		return nodeCA && (nodeCA.$Type !== "CustomAttribute");
	}

	/* return true if attribute type field should be disabled */
	get isBuiltInOrUpdated(): boolean {
		const { isBuiltInAttribute, isNewFromInherited, nodeCA } = this;

		return isNewFromInherited ||
			isBuiltInAttribute ||
			isConfigUpdatedAttribute(nodeCA) ||
			
			// editing an attribute created using New from Inherited
			xml.nodeAttributeIsReadOnly(nodeCA, ATTR_VALUETYPE);
	}

	/** return false if Unit of Measurements settings should be visible */
	get isDouble(): boolean {
		return this.attrType !== VALUE_TYPE.DOUBLE;
	}
	
	/** return select options for owner type if BO is for a revisable class */
	@computed({ keepAlive: true }) get ownerOptions(): TBaseItem[] | undefined {
		const { loc, nodeBO } = this;
		const rc = getClass(nodeBO.RootClassName);
		
		if (rc.isMaster()) {
			return [
				createSelectOptionByType(rc.revisionType, loc, true),
				createSelectOptionByType(rc.type, loc, true),
			];
		}
	}

	/** return options for REST Access select */
	@computed get restAccessOptions(): RadioButtonOptionsType[] {
		const { locale } = this;
		const { labelRESTNone, labelRESTRead, labelRESTWrite } = locale;
		const options = [];
		options.push(createRadioOption(REST_NONE, labelRESTNone));
		options.push(createRadioOption(REST_READ, labelRESTRead));
		options.push(createRadioOption(REST_WRITE, labelRESTWrite));
		return options;
	}

	/** disable save button if nothing changed */
	get saveDisabled(): boolean {
		return this.isEdit && !this.isAttrChanged;
	}
	
	/** options for selecting a target class */
	@computed get targetClassOptions(): TBaseItem[] {
		const { isRefType, loc, targetClass } = this;

		if (!isRefType) {
			return [];
		}

		const available = getClasses().filter(({ canSelectAsTargetClass, type }) => canSelectAsTargetClass || (type === targetClass));
		const items = available.map(({ type }) => createSelectOptionByType(type, loc, true));

		return sortBy(items, "name");
	}
	
	/** store containing options for selecting a subtype of the target class */
	@computed get targetClassSubtypeDataStore(): DataStoreNamesStore | undefined {
		const target = TARGET_CLASS_NAMES_WITH_SUBTYPE[this.targetClass];
		if (target) {
			const dataStoreId = globalDataStoreId(target);
			return new DataStoreNamesStore({ dataStoreId });
		}
	}

	/** options for selecting an UOM */
	@computed get uomListDataStore(): DataStoreNamesStore {
		const { attrUOMType } = this;
		if (!this._uomListDataStore && attrUOMType) {
			const dataStoreId = globalDataStoreId(UNIT_OF_MEASURE);
			const q = new Query();
			q.RefAttr(xml.ATTR_PARENT, Operator.EQUALS, attrUOMType);
			this._uomListDataStore = new DataStoreNamesStore({ dataStoreId, queryXml: q.toString() });
		}

		return this._uomListDataStore;
	}		

	/** options for selecting an UOM */
	get uomTypeListDataStore(): DataStoreNamesStore {
		if (!this._uomTypeListDataStore) {
			const dataStoreId = globalDataStoreId(UNIT_OF_MEASURE_TYPE);
			this._uomTypeListDataStore = new DataStoreNamesStore({ dataStoreId });
		}
		return this._uomTypeListDataStore;
	}		

	/** copy options are disabled when locked */
	get isCopyOptionsDisabled(): boolean {
		const { isEdit, nodeCA } = this;
		return isEdit && nodeCA[ATTR_COPY_CATEGORIES_LOCKED];
	}

	/** return true is selected attribute type is enum of enum container */
	get isEnumType(): boolean {
		return isEnumValueType(this.attrType);
	}

	@computed get isCopyOptionsNameVisible(): boolean {
		const { attrCopyCategories } = this;
		return Object.keys(attrCopyCategories).some((rootType) => {
			return attrCopyCategories[rootType] === COPY_CATEGORY_ENUM_OPTIONAL;
		});
	}

	/** return true if class is Style or Member of Style BO */
	get isStyleBO(): boolean {
		return this.nodeBO.DomainClassName === "Style";
	}

	/** indicates if attribute type cannot be changed */
	get isTypeDisabled(): boolean {
		const { attrUOMEnabled, isBuiltInOrUpdated, isEdit } = this;
		return isBuiltInOrUpdated || (isEdit && attrUOMEnabled);
	}

	/** return true if all form fields are populated correctly */
	get isValid(): boolean {
		const {
			attrCopyOptionsNameError,
			attrExpressionError,
			attrIdError,
			attrInitialValueError,
			attrSelectEnumerationError,
			attrUOMError,
			attrUOMTypeError,
			attrValidationExpressionError,
			targetClassError,
			targetClassSubtypeError,
		} = this;

		return !attrCopyOptionsNameError &&
			!attrExpressionError &&
			!attrIdError &&
			!attrInitialValueError &&
			!attrSelectEnumerationError &&
			!attrUOMError &&
			!attrUOMTypeError &&
			!attrValidationExpressionError &&
			!targetClassError &&
			!targetClassSubtypeError;
	}
	
	/** return REST API Exposed for server save operation */
	get RESTAPIExposed(): boolean {
		return this.attrRESTAccess !== REST_NONE;
	}

	/** return REST API Restricted for server save operation */
	get RESTAPIRestricted(): boolean {
		return this.attrRESTAccess === REST_READ;
	}

	/** return REST API Update for server save operation */
	get RESTAPIUpdate(): boolean {
		return this.attrRESTAccess === REST_WRITE;
	}

	/** return options for attribute type selection */
	@computed({ keepAlive: true }) get typeOptions(): TBaseItem[] {
		const { loc } = this;
		const res = TYPES.map((type) => ({
			id: type,
			name: loc.getAttributeType(type),
		}));
		return orderBy(res, "name");
	}

	/** set and validate attribute id */
	@action onChangeAttrId: ChangeEventHandler<HTMLInputElement> = (e) => {
		this.attrId = e.target.value;
		this.validateAttributeId();
	};

	/** set attribute name */
	@action onChangeAttrName: ChangeEventHandler<HTMLInputElement> = (e) => {
		this.attrName = e.target.value;
	};

	/** set attribute format */
	@action onChangeAttrFormat = (event: SyntheticEvent<Element, Event>, item?: TBaseItem | null): void => {
		this.attrFormat = item?.id || ""; // when using clear button item is null
		this.attrInitialValue = this.defaultInitialValue;
		this.attrInitialValueError = "";
		this.columnWidth = this.columnWidthDefault;
	};

	/** set attribute tooltip */
	@action onChangeAttrTooltip: ChangeEventHandler<HTMLInputElement> = (e) => {
		this.attrTooltip = e.target.value;
	};

	/** set attr enabled */
	@action onChangeAutofillDependent = (checked: boolean): void => {
		this.attrAutofillDependent = checked;
	};
	
	/** set attr hidden */
	@action onChangeHidden = (checked: boolean): void => {
		this.attrHidden = checked;
	};

	/** set attribute type */
	@action onChangeAttrType = (event: SyntheticEvent<Element, Event>, item: TBaseItem): void => {
		// make sure to process lines in this order as the default format and value depend on the type
		this.attrType = item.id as C8AttributeType;
		this.attrFormat = this.defaultFormat;
		this.attrInitialValue = this.defaultInitialValue;
		this.attrInitialValueError = "";

		this.columnWidth = this.columnWidthDefault;
		this.dependsOnAttribute = undefined;
		this.dependsOnEnumList = undefined;
		this.dependsOnSubtypeUrl = undefined;

		if (this.attrUOMEnabled) {
			this.attrUOM = "";
			this.attrUOMEnabled = false;
			this.attrUOMError = "";
			this.attrUOMType = "";
			this.attrUOMTypeError = "";
		}

		if (!this.isEnumType) {
			this.attrSelectEnumeration = "";
			this.attrSelectEnumerationCnl = "";
			this.attrSelectEnumerationError = "";
		}

		if (!this.isRefType) {
			this.targetClass = "";
			this.targetClassError = "";
			this.targetClassSubtype = undefined;
			this.targetClassSubtypeError = "";
		}
	};

	/** set copyCategory */
	@action onChangeCopyCategory = (value: string, rootType: C8NodeType): void => {
		this.attrCopyCategories[rootType] = value;
		if (!this.isCopyOptionsNameVisible) {
			this.attrCopyOptionsName = "";
		}
	};

	/** set copy options name */
	@action onChangeCopyOptionsName: ChangeEventHandler<HTMLInputElement> = (e) => {
		this.attrCopyOptionsName = e.target.value;
	};

	/** set depends on attribute */
	@action onChangeDependsOnAttribute = (event: SyntheticEvent<Element, Event>, item?: TBaseItem | null): void => {
		this.dependsOnAttribute = item;
		this.attrAutofillDependent = false;
	};

	/** set attr disable drag edits */
	@action onChangeDisableDragEdits = (checked: boolean): void => {
		this.attrDisableDragEdits = checked;
	};	

	/** set column width */
	@action onChangeColumnWidth = (event: SyntheticEvent<Element, Event>, item: TBaseItem): void => {
		this.columnWidth = item.id;
	};

	/** set enable comments */
	@action onChangeEnableComments = (checked: boolean): void => {
		this.attrEnableComments = checked;
	};

	/** set attr enabled */
	@action onChangeEnabled = (checked: boolean): void => {
		this.attrEnabled = checked;
	};

	/** set enum initial value */
	@action onChangeEnumInitialValue = (event: SyntheticEvent<Element, Event>, item?: TBaseItem | null): void => {
		// no validation needed for the initial value of an enum
		this.attrInitialValue = item?.id;
	};

	/** set expression and update state */
	@action onChangeExpression: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
		const { value } = e.target;

		this.attrExpression = value;

		if (!this.attrExpressionEnabled) {
			this.expressionState = value ? EXPRESSION_RUNTIME_ENUM_DISABLED : EXPRESSION_RUNTIME_ENUM_EMPTY;
		}
	};

	/** set attr expression enabled */
	@action onChangeExpressionEnabled: ChangeEventHandler<HTMLInputElement> = (e) => {
		if (e.target.checked) {
			this.expressionState = EXPRESSION_RUNTIME_ENUM_CONTINUOUS;
		} else if (this.attrExpression) {
			this.expressionState = EXPRESSION_RUNTIME_ENUM_DISABLED;
		} else {
			this.expressionState = EXPRESSION_RUNTIME_ENUM_EMPTY;
		}
	};

	/** set enable comments */
	@action onChangeExpressionEvaluateOnClient = (checked: boolean): void => {
		this.attrExpressionEvaluateOnClient = checked;
	};
	
	/** set expression */
	@action onChangeExpressionState = (value: string): void => {
		this.expressionState = value;
	};

	/** set initial value */
	@action onChangeInitialValue = (value: boolean | Date | number | string | undefined): void => {
		if (value instanceof Date) {
			value = value.getTime();
		}

		this.attrInitialValue = value ?? this.defaultInitialValue;
		this._validateInitialValue();
	};

	/** set notes */
	@action onChangeNotes: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
		this.attrNotes = e.target.value;
	};

	/** set owner type */
	@action onChangeOwnerClass = (event: SyntheticEvent<Element, Event>, item: TBaseItem): void => {
		this.ownerClass = item.id;
		this.validateAttributeId();
	};

	/** set attr required */
	@action onChangeRequired = (checked: boolean): void => {
		this.attrRequired = checked;
	};

	/** set attr readonly */
	@action onChangeReadonly = (checked: boolean): void => {
		this.attrReadonly = checked;
		// Disable Drag Edits will be hidden if attribute is read only, so set it to default false value
		if (checked) {
			this.attrDisableDragEdits = false;
		}
	};

	/** set REST Access */
	@action onChangeRESTAccess = (newValue: string): void => {
		this.attrRESTAccess = newValue;
	};

	/** set select enumeration */
	@action onChangeSelectEnumeration = async (event: SyntheticEvent<Element, Event>, item?: TBaseItem | null): Promise<void> => {
		this.attrSelectEnumeration = item?.name;
		this.attrSelectEnumerationCnl = item?.id;
		this.attrInitialValue = this.defaultInitialValue;
		this.attrInitialValueError = "";
		this.dependsOnAttribute = undefined;
		this.dependsOnEnumList = undefined;
		
		if (item) {
			this.attrSelectEnumerationError = "";
			await this._initEnumDependsOn();
		}
	};
	
	/** set attr allow subtype configuration */
	@action onChangeSubtypeConfiguration = (checked: boolean): void => {
		this.attrSubtypeConfiguration = checked;
	};

	/** set target class */
	@action onChangeTargetClass = (event: SyntheticEvent<Element, Event>, item?: TBaseItem | null): void => {
		this.targetClass = item?.id || "";
		this.targetClassSubtype = undefined;
		this.targetClassSubtypeError = "";
		this.dependsOnAttribute = undefined;
		this.dependsOnEnumList = undefined;
		this.dependsOnSubtypeUrl = undefined;

		if (item) {
			this.targetClassError = "";
		}

		// if target class is now image, then reset columnWidth to empty;
		// if target class was image (empty columnWidth), then populate it with default
		if ((item?.id === TYPE_IMAGE) || !this.columnWidth) {
			this.columnWidth = this.columnWidthDefault;
		}
	};

	/** set target class subtype */
	@action onChangeTargetClassSubtype = (event: SyntheticEvent<Element, Event>, item?: TBaseItem | null): void => {
		this.targetClassSubtype = item;
		this.dependsOnAttribute = undefined;
		this.dependsOnEnumList = undefined;
		this._initRef();

		if (item) {
			this.targetClassSubtypeError = "";
		}
	};

	/** set validation expression */
	@action onChangeValidationExpression: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
		this.attrValidationExpression = e.target.value;
	};

	/** set validation expression */
	@action onChangeValidationErrorMessage: ChangeEventHandler<HTMLInputElement> = (e) => {
		this.attrValidationErrorMessage = e.target.value;
	};

	/** set select enumeration */
	@action onChangeUOM = (event: SyntheticEvent<Element, Event>, item: TBaseItem): void => {
		this.attrUOM = item?.id;
		this.attrUOMName = item?.name;
	};

	/** set attr hidden */
	@action onChangeUOMEnabled = (checked: boolean): void => {
		this.attrUOMEnabled = checked;

		if (checked) {
			this.attrExpressionEvaluateOnClient = false;
			this.attrHidden = false;
			this.attrReadonly = false;
		} else {
			this.attrUOM = "";
			this.attrUOMError = "";
			this.attrUOMType = "";
			this.attrUOMTypeError = "";
		}
	};

	/** set select enumeration */
	@action onChangeUOMType = (event: SyntheticEvent<Element, Event>, item: TBaseItem): void => {
		this.attrUOMType = item.id;
		this.attrUOMTypeName = item.name;
		this.attrUOM = "";
		this.attrUOMError = "";
		this.attrUOMTypeError = "";
		this._uomListDataStore = null;
	};

	/** dojo Edit Enumerations view have created, preselect user's enum */
	@action onEditEnumerationsViewCreated = (view: SwitchboardWidget): void => {
		this.editEnumerationView = view;
	};

	/** open Modal popup with Edit Enumerations view */
	@action onEditEnumerationsClickOpen = (): void => {
		this.openEnumerationsModal = true;
	};

	/** close popup with Edit Enumerations view */
	@action onEditEnumerationsClickClose = (): void => {
		const { attrSelectEnumeration, editEnumerationView } = this;
		const node = editEnumerationView.getSwitchboardSelectedNode();
		const enumList = node?.$Name;

		if (enumList && (enumList !== attrSelectEnumeration)) {
			this.attrSelectEnumeration = enumList;
			this.attrSelectEnumerationCnl = node.$URL;
			this.attrInitialValue = this.defaultInitialValue;
			this.attrInitialValueError = "";
		}

		this.openEnumerationsModal = false;
		this.editEnumerationView = null;

		// reset store so that it will contain any new enumlists created while dialog was open
		this._enumListDataStore = null;
	};

	/** set form error message */
	@action setFormError = (err: C8RequestError): void => {
		const { attrId, loc, nodeBO } = this;
		const { message, csiErrorCode, csiErrorParams } = err;
		const validationCode = `${nodeBO.$Name}.Attr.${attrId}_IV`;

		if (csiErrorCode === "SRV_ERR_RollupExpression_EXPRESSION_ERROR") {
			this.attrValidationExpressionError = csiErrorParams.join(";");
		} else if (csiErrorCode === validationCode) {
			this.formError = loc.resource("AttributeDefinitionValidationError");
		} else {
			this.formError = message;
		}
	};

	/** remove leading and trailing spaces */
	@action trim(): void {
		this.attrId = this.attrId.trim();
		this.attrName = this.attrName.trim();

		if (typeof this.attrInitialValue === "string") {
			this.attrInitialValue = this.attrInitialValue.trim();
		}
	}

	/** validate form fields, return false if there validation false and set fields errors msg */
	@action validate(): boolean {
		this.trim();

		const {
			attrCopyOptionsName,
			attrExpression,
			attrExpressionEnabled,
			attrFormat,
			attrId,
			attrInitialValue,
			attrSelectEnumeration,
			attrType,
			attrUOM,
			attrUOMEnabled,
			attrUOMType,
			attrValidationExpression,
			cache,
			initialValueHidden,
			isCopyOptionsNameVisible,
			isEnumType,
			isNewFromInherited,
			loc,
			nodeCA,
			nodeBO,
			ownerClass,
			targetClass,
			targetClassSubtype,
		} = this;

		// reset form error when starting save
		this.formError = "";

		if (!isNewFromInherited) {
			// none of the following are editable when using New From Inherited
			this.attrIdError = validateWithError(() => {
				validateRequiredStringValue(loc, attrId);
				validateAttributeId(loc, nodeCA, nodeBO, ownerClass, attrId, cache);
			});

			this.attrSelectEnumerationError = validateWithError(() => isEnumType && validateSelectEnumeration(loc, attrSelectEnumeration));
			this.targetClassError = validateWithError(() => isRefValueType(attrType) && validateRequiredStringValue(loc, targetClass));
			this.targetClassSubtypeError = validateWithError(() => isRefValueType(attrType) && TARGET_CLASS_NAMES_WITH_SUBTYPE[targetClass] && validateRequiredStringValue(loc, targetClassSubtype?.id));
		}

		this.attrCopyOptionsNameError = validateWithError(() => isCopyOptionsNameVisible && validateCopyOptions(loc, attrCopyOptionsName));
		this.attrExpressionError = validateWithError(() => attrExpressionEnabled && validateRequiredStringValue(loc, attrExpression));
		this.attrInitialValueError = validateWithError(() => !initialValueHidden && validateInitialValue(attrInitialValue, attrType, attrFormat));
		this.attrValidationExpressionError = validateWithError(() => attrValidationExpression && validateValidationExpression(loc, attrInitialValue, attrValidationExpression));
		this.attrUOMError = validateWithError(() => attrUOMEnabled && validateRequiredStringValue(loc, attrUOM));
		this.attrUOMTypeError = validateWithError(() => attrUOMEnabled && validateRequiredStringValue(loc, attrUOMType));

		const valid = this.isValid;

		if (!valid) {
			focusOnFirstInvalidInputField(this.formRef.current);
		}

		return valid;
	}

	/** validate attribute id against the domain object */
	@action validateAttributeId = (): void => {
		const { attrId, cache, nodeCA, nodeBO, loc, ownerClass } = this;
		this.attrIdError = validateWithError(() => validateAttributeId(loc, nodeCA, nodeBO, ownerClass, attrId, cache));
	};
	
	/**
	 * do server request to create new attribute
	 */
	async saveForm(): Promise<JsonResponse> {
		const { 
			attrFormat,
			attrId,
			attrName, 
			attrTooltip,
			attrUOM,
			attrUOMEnabled,
			attrUOMType,
			attrValidationErrorMessage,
			attrValidationExpression,
			cache,
			isAttrChanged,
			isEdit,
			nodeBO,
			ownerClass,
		} = this;

		if (isEdit && !isAttrChanged) {
			return;
		}

		const { ATTR_TYPES, ATTR_VALIDATION, TOOLTIP, TYPE_ATTR } = siteDefaultLocale;
		const w = new Writer();
		const url = this.nodeCA?.$URL ?? BusinessObject.getBOCustomAttributeURL(nodeBO.$Name, attrId);

		if (!isEdit && attrUOMEnabled) {
			// create Unit of Measure attribute (special read only attribute to hold UOM data)
			this.saveAttribute(w, url, true);

			// disable row editing for UOM conversion attribute
			w.changeNodeFlags(url + UOM_SUFFIX, xml.NODE_FLAG_NOEDIT);

			w.callMethod("SiteAdmin", "AddEntriesToUOMModuleCreateLists")
				.addParameter("UOMType", xml.ATTR_REF, attrUOMType)
				.addParameter("UnitOfMeasure", xml.ATTR_REF, attrUOM)
				.addParameter("AttributeName", xml.ATTR_STRING, attrId)
				.addParameter("ReflectionAttributeOwner", xml.ATTR_STRING, ownerClass);
		}

		// create normal attribute
		this.saveAttribute(w, url, false);

		// Set attribute localized (site locale) name if localized name was supplied
		if (attrName) {
			siteDefaultLocale.setResource(w, ATTR_TYPES, [ownerClass, "Attr", attrId], attrName, null, true);
		}

		if (attrTooltip) {
			const keysTooltip = [ownerClass, TYPE_ATTR, attrId + TOOLTIP];
			siteDefaultLocale.setResource(w, ATTR_TYPES, keysTooltip, attrTooltip, null, true);
		}

		if (attrValidationErrorMessage) {
			const keysValidate = [ownerClass, TYPE_ATTR, attrId + ATTR_VALIDATION];
			siteDefaultLocale.setResource(w, ATTR_TYPES, keysValidate, attrValidationErrorMessage, null, true);
		}

		if (attrValidationExpression && (attrFormat !== FORMAT_RICH)) {
			w.callMethod("Reflection", "ValidateInitialValue").addParameter(xml.PROP_URL, xml.ATTR_REF, url);
		}

		// perform attribute validation on server
		w.validateNode(url, true);

		const resultNodes = this.resultNodes = [];
		const res = await submitWriter(w);
		cache.cacheResponse(res);
		
		// track new nodes
		resultNodes.push(cache.get(url));

		if (attrUOMEnabled) {
			resultNodes.push(cache.get(url + UOM_SUFFIX));
		}

		return res;
	};

	saveAttribute(w: Writer, url: cnl, isUOMAttribute: boolean): void {
		const { 
			attrAutofillDependent,
			attrCopyCategories,
			attrCopyOptionsName,
			attrDisableDragEdits,
			attrEnableComments,
			attrEnabled,
			attrExpression,
			attrExpressionEvaluateOnClient,
			attrFormat, 
			attrHidden,
			attrId, 
			attrNotes,
			attrReadonly, 
			attrRequired, 
			attrSelectEnumeration,
			attrSubtypeConfiguration, 
			attrType, 
			attrValidationExpression,
			columnWidth, 
			dependsOnAttribute,
			dependsOnAttributePath,
			expressionState,
			isEdit,
			isEnumType,
			isNewFromInherited,
			nodeBO,
			nodeCA,
			ownerClass,
			RESTAPIExposed,
			RESTAPIRestricted,
			RESTAPIUpdate,
			targetClass,
			targetClassSubtype,
			_getInitialValueForSave,
		} = this;
		const { SMALL } = types;

		const isCustom = isCustomAttribute(nodeCA);
		const initialValue = _getInitialValueForSave(isUOMAttribute);
		const format = isEnumType ? attrSelectEnumeration :
			(TARGET_CLASS_AS_FORMAT.has(targetClass) ? targetClass : attrFormat);

		// attributes which have different meaning or value for normal attribute and for UOM attribute
		const _attrId = isUOMAttribute? attrId + UOM_SUFFIX : attrId;
		const _attrType = isUOMAttribute ? VALUE_TYPE.REF : attrType;
		const _url = isUOMAttribute? url + UOM_SUFFIX : url;
		const _format = isUOMAttribute ? "": format;
		const _columnWidth = isUOMAttribute ? SMALL : columnWidth;
		const _attrRequired = isUOMAttribute ? true : attrRequired;
		const _attrValidationExpression = isUOMAttribute ? "" : attrValidationExpression;
		const _attrExpression = isUOMAttribute? "" : attrExpression;
		const _expressionState = isUOMAttribute ? EXPRESSION_RUNTIME_ENUM_EMPTY : expressionState;
		const _attrNotes = isUOMAttribute ? "" : attrNotes;
		const _targetClass = isUOMAttribute? CLASS_UOM_CONVERSION : targetClass;

		// create attribute
		if (!isEdit) {
			w.callMethod("Reflection", "CreateCustomAttribute")
				.addParameter("ParentURL", xml.ATTR_REF, nodeBO.$URL)
				.addParameter("Id", xml.ATTR_STRING, _attrId);
		}

		// set new attribute properties
		const cn = w.changeNode(_url)
			// owner
			.changeAttributeFlag(ATTR_REFLECTION_ATTR_OWNER, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
			.changeAttribute(ATTR_REFLECTION_ATTR_OWNER, xml.ATTR_STRING, ownerClass)
			// id
			.changeAttributeFlag(xml.ATTR_NAME, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
			.changeAttribute(xml.ATTR_NAME, xml.ATTR_STRING, _attrId)
			// type
			.changeAttributeFlag(ATTR_VALUETYPE, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
			.changeAttribute(ATTR_VALUETYPE, xml.ATTR_STRING, _attrType)
			// depends on
			.changeAttributeFlag(ATTR_DEPENDS_ON, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
			.changeAttribute(ATTR_DEPENDS_ON, xml.ATTR_REF, dependsOnAttribute?.id ?? DUMMY)
			// depends on path
			.changeAttributeFlag(ATTR_DEPENDS_ON_PATH, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
			.changeAttribute(ATTR_DEPENDS_ON_PATH, xml.ATTR_STRING, dependsOnAttributePath)			
			// format
			.changeAttributeFlag(ATTR_FORMAT, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
			.changeAttribute(ATTR_FORMAT, xml.ATTR_STRING, _format)
			// column width
			.changeAttributeFlag(ATTR_COLUMN_WIDTH, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
			.changeAttribute(ATTR_COLUMN_WIDTH, xml.ATTR_ENUM, xml.enumCreate("ColumnWidthType", _columnWidth))
			// initial value
			.changeAttributeFlag(ATTR_VALUE, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
			.changeAttribute(ATTR_VALUE, xml.ATTR_STRING, initialValue)
			// required
			.changeAttributeFlag(ATTR_REQUIRED, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
			.changeAttribute(ATTR_REQUIRED, xml.ATTR_BOOLEAN, _attrRequired)
			// read only
			.changeAttributeFlag(BusinessObject.ATTR_ATTRIBUTE_FLAGS, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
			.changeAttribute(BusinessObject.ATTR_ATTRIBUTE_FLAGS, xml.ATTR_INT64, attrReadonly ? xml.ATTR_FLAG_NOEDIT : 0)
			// optional aka Allow subtype dependent configuration
			.changeAttributeFlag(ATTR_OPTIONAL, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
			.changeAttribute(ATTR_OPTIONAL, xml.ATTR_BOOLEAN, attrSubtypeConfiguration)
			// enabled
			.changeAttributeFlag(ATTR_ENABLED, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
			.changeAttribute(ATTR_ENABLED, xml.ATTR_BOOLEAN, attrEnabled)
			// disable drag edits aka restrict drag edits
			.changeAttributeFlag(BusinessObject.ATTR_DRAG_EDIT_DISABLED, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
			.changeAttribute(BusinessObject.ATTR_DRAG_EDIT_DISABLED, xml.ATTR_BOOLEAN, attrDisableDragEdits)
			// enable comments aka restrict comments
			.changeAttributeFlag(ATTR_COMMENT_RESTRICTED, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
			.changeAttribute(ATTR_COMMENT_RESTRICTED, xml.ATTR_BOOLEAN, !attrEnableComments)
			// hidden
			.changeAttributeFlag(ATTR_HIDDEN, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
			.changeAttribute(ATTR_HIDDEN, xml.ATTR_BOOLEAN, attrHidden)
			// validation expression
			.changeAttributeFlag(ATTR_VALIDATION_EXPRESSION, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
			.changeAttribute(ATTR_VALIDATION_EXPRESSION, xml.ATTR_STRING, _attrValidationExpression)
			// expression
			.changeAttributeFlag(ATTR_EXP, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
			.changeAttribute(ATTR_EXP, xml.ATTR_STRING, _attrExpression)
			// evaluate expression on client
			.changeAttributeFlag(ATTR_EXP_ONCLIENT_DISABLED, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
			.changeAttribute(ATTR_EXP_ONCLIENT_DISABLED, xml.ATTR_BOOLEAN, !attrExpressionEvaluateOnClient)
			// evaluate expression state
			.changeAttributeFlag(ATTR_EXPRESSION_STATE, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
			.changeAttribute(ATTR_EXPRESSION_STATE, xml.ATTR_ENUM, _expressionState)
			// notes
			.changeAttributeFlag(ATTR_NOTES, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
			.changeAttribute(ATTR_NOTES, xml.ATTR_STRING, _attrNotes)
			// copy options
			.changeAttribute(ATTR_COPY_CATEGORIES, xml.ATTR_ENUMMAP, getCopyCategoriesToSave(attrCopyCategories))
			.changeAttribute(ATTR_COPY_OPTION_NAME, xml.ATTR_STRING, attrCopyOptionsName)
			// REST API Restricted
			.changeAttributeFlag(ATTR_REST_RESTRICTED, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
			.changeAttribute(ATTR_REST_RESTRICTED, xml.ATTR_BOOLEAN, RESTAPIRestricted)
			// REST API Update
			.changeAttributeFlag(ATTR_REST_UPDATE, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
			.changeAttribute(ATTR_REST_UPDATE, xml.ATTR_BOOLEAN, RESTAPIUpdate);

		if (isCustom) {
			// update target class information
			cn.changeAttributeFlag(ATTR_TARGET_CLASS_NAME, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
				.changeAttribute(ATTR_TARGET_CLASS_NAME, xml.ATTR_STRING, _targetClass ?? "")
				.changeAttributeFlag(ATTR_TARGET_CLASS_SUBTYPE_ID, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
				.changeAttribute(ATTR_TARGET_CLASS_SUBTYPE_ID, xml.ATTR_STRING, targetClassSubtype ? ATTR_SUBTYPE : "")
				.changeAttributeFlag(ATTR_SUBTYPE, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
				.changeAttribute(ATTR_SUBTYPE, xml.ATTR_REF, targetClassSubtype?.id ?? DUMMY);

			// REST API Exposed
			cn.changeAttributeFlag(ATTR_REST_EXPOSED, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
				.changeAttribute(ATTR_REST_EXPOSED, xml.ATTR_BOOLEAN, RESTAPIExposed);
		}

		if (isNewFromInherited) {
			// mark attributes read-only that cannot be changed when performing New from Inherited
			cn.changeAttributeFlag(ATTR_VALUETYPE, xml.ATTR_FLAGS_READONLY, true, false)
				.changeAttributeFlag(ATTR_SUBTYPE, xml.ATTR_FLAGS_READONLY, true, false)
				.changeAttributeFlag(ATTR_TARGET_CLASS_NAME, xml.ATTR_FLAGS_READONLY, true, false);
			
			if (isEnumType) {
				cn.changeAttributeFlag(ATTR_FORMAT, xml.ATTR_FLAGS_READONLY, true, false);
			}
		}

		if (dependsOnAttribute) {
			// autofill dependent
			cn.changeAttributeFlag(ATTR_AUTOFILL_DEPENDENT_ENUM, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true);
			cn.changeAttribute(ATTR_AUTOFILL_DEPENDENT_ENUM, xml.ATTR_BOOLEAN, attrAutofillDependent);
		}

		// set attribute flag "modified"
		cn.changeAttribute(BusinessObject.ATTR_MODIFIED, xml.ATTR_BOOLEAN, true);
	}

	/** reset store to initialize state */
	@action resetStore(): void {
		const { nodeBO } = this;
		const rc = getClass(nodeBO.RootClassName);

		// do not alphabetize, order matter
		this.attrType = VALUE_TYPE.STRING;
		this.attrFormat = "";

		this.attrAutofillDependent = false;
		this.attrCopyCategories = getCopyCategoriesForRuntime({});
		this.attrCopyOptionsName = "";
		this.attrCopyOptionsNameError = "";
		this.attrDisableDragEdits = false;
		this.attrEnableComments = false;
		this.attrEnabled = true;
		this.attrExpression = "";
		this.attrExpressionError = "";
		this.attrExpressionEvaluateOnClient = false;
		this.attrHidden = false;
		this.attrId = "";
		this.attrIdError = "";
		this.attrInitialValue = this.defaultInitialValue; // initial value initialization should be after type and format
		this.attrInitialValueError = "";
		this.attrName = "";
		this.attrNotes = "";
		this.attrRESTAccess = REST_NONE;
		this.attrReadonly = false;
		this.attrRequired = false;
		this.attrSelectEnumeration = "";
		this.attrSelectEnumerationCnl = "";
		this.attrSelectEnumerationError = "";
		this.attrSubtypeConfiguration = false;
		this.attrTooltip = "";
		this.attrUOM = "";
		this.attrUOMEnabled = false;
		this.attrUOMError = "";
		this.attrUOMType = "";
		this.attrUOMTypeError = "";
		this.attrValidationErrorMessage = "";
		this.attrValidationExpression = "";
		this.attrValidationExpressionError = "";
		this.columnWidth = this.columnWidthDefault;
		this.dependsOnAttribute = undefined;
		this.dependsOnEnumList = undefined;
		this.dependsOnSubtypeUrl = undefined;
		this.expressionState = EXPRESSION_RUNTIME_ENUM_EMPTY;
		this.formError = "";
		this.openEnumerationsModal = false;
		this.ownerClass = rc.isMaster() ? nodeBO.AttributeClassName : rc.type;
		this.targetClass = "";
		this.targetClassSubtype = undefined;
	};

	/** return initial value for expression state */
	private _getInitialExpressionState = (nodeCA: C8Node): string => {
		const { attrExpression } = this;
		const expressionState = nodeCA[ATTR_EXPRESSION_STATE];

		if (attrExpression && (expressionState === EXPRESSION_RUNTIME_ENUM_EMPTY)) {
			// if there is an expression and no state, return continuous
			return EXPRESSION_RUNTIME_ENUM_CONTINUOUS;
		} else if (!attrExpression && (expressionState !== EXPRESSION_RUNTIME_ENUM_EMPTY)) {
			// if there is not an expression and some state, return empty state
			return EXPRESSION_RUNTIME_ENUM_EMPTY;
		}

		return expressionState;
	};

	/** return initial value to save to attribute node */
	private _getInitialValueForSave = (isUOMAttribute?: boolean): string => {
		const { attrInitialValue, attrSelectEnumeration, attrType, attrUOMName, attrUOMTypeName, isEnumType } = this;
		let initialValue = attrInitialValue;

		if (isEnumType) {
			// if select type is enum (not container), then format and initial value should be special for enum
			// for enum container type (map, list, vector) initial value is empty array
			initialValue = isContainerValueType(attrType) ? [] : ((attrInitialValue as string) || xml.enumCreate(attrSelectEnumeration));
		} else if (isUOMAttribute) {
			// if type is double and unit of measure is enabled use initial value to store conversion URL
			initialValue = generateUOMConversionURL(attrUOMTypeName, attrUOMName, attrUOMName);
		}

		return JSON.stringify(initialValue);
	};

	/** load values from attribute node, this function is only used when editing attribute */
	@action private _initFromNode(nodeCA: C8Node, isNewFrom: boolean): void {
		// do not destruct class properties here, as properties must be initialized in particular order
		const { nodeBO, _getInitialExpressionState } = this;
		const [targetClass, targetClassSubtypeUrl] = getAttributeTargetClassAndSubtype(nodeCA);
		const attrId = Locale.getConfigurableAttributeId(nodeCA);
		let ownerClass = Locale.getConfigurableAttributeType(nodeCA);

		if (isNewFrom) {
			ownerClass = getOwnerClassForBusinessObject(nodeBO, ownerClass);
		}

		// do not alphabetize, order matter
		this.attrType = nodeCA[ATTR_VALUETYPE];
		this.attrFormat = nodeCA[ATTR_FORMAT] || "";

		this.attrAutofillDependent = nodeCA[ATTR_AUTOFILL_DEPENDENT_ENUM];
		this.attrCopyCategories = getCopyCategoriesForRuntime(nodeCA[ATTR_COPY_CATEGORIES]);
		this.attrCopyOptionsName = nodeCA[ATTR_COPY_OPTION_NAME];
		this.attrCopyOptionsNameError = "";
		this.attrDisableDragEdits = nodeCA[BusinessObject.ATTR_DRAG_EDIT_DISABLED];
		this.attrEnableComments = !nodeCA[ATTR_COMMENT_RESTRICTED];
		this.attrEnabled = nodeCA[ATTR_ENABLED];
		this.attrExpression = nodeCA[ATTR_EXP];
		this.attrExpressionError = "";
		this.attrExpressionEvaluateOnClient = !nodeCA[ATTR_EXP_ONCLIENT_DISABLED];
		this.attrHidden = nodeCA[ATTR_HIDDEN];
		this.attrId = attrId;
		this.attrIdError = "";
		this.attrInitialValue = JSON.parse(nodeCA[ATTR_VALUE]); // initial value initialization should be after type and format
		this.attrInitialValueError = "";
		this.attrName = Locale.getConfigurableAttributeNodeName(nodeCA);
		this.attrNotes = nodeCA[ATTR_NOTES];
		this.attrRESTAccess = nodeCA[ATTR_REST_EXPOSED] ? (nodeCA[ATTR_REST_RESTRICTED] ? REST_READ: REST_WRITE) : REST_NONE;
		this.attrReadonly = !!(nodeCA[BusinessObject.ATTR_ATTRIBUTE_FLAGS] & xml.ATTR_FLAG_NOEDIT);
		this.attrRequired = nodeCA[ATTR_REQUIRED];
		this.attrSelectEnumeration = this.isEnumType ? nodeCA[ATTR_FORMAT] : ""; 
		this.attrSelectEnumerationCnl = "";
		this.attrSelectEnumerationError = "";
		this.attrSubtypeConfiguration = nodeCA[ATTR_OPTIONAL];
		this.attrTooltip = Locale.getTypeAttributeResource(ownerClass, attrId, Locale.TOOLTIP);
		this.attrValidationErrorMessage = Locale.getTypeAttributeResource(ownerClass, attrId, Locale.ATTR_VALIDATION);
		this.attrValidationExpression = nodeCA[ATTR_VALIDATION_EXPRESSION];
		this.attrValidationExpressionError = "";
		this.columnWidth = xml.enumGetText(nodeCA[ATTR_COLUMN_WIDTH]) || this.columnWidthDefault;
		this.expressionState = _getInitialExpressionState(nodeCA);
		this.formError = "";
		this.openEnumerationsModal = false;
		this.ownerClass = ownerClass;
		this.targetClass = targetClass;

		// initialize Unit of Measure data
		this._initUOMEdit(nodeCA);

		try {
			// initialize enum and ref specific properties
			this._initEnum();
			this._initRef(targetClassSubtypeUrl);
			this._initDependsOn(nodeCA[ATTR_DEPENDS_ON]);
		} catch (ex) {
			console.warn(`Could not initialize attribute: ${nodeCA.$URL}`);
		}
	}

	/** initialize UOM data for editing, this function should not be called for new attribute, only for editing */
	@action private async _initUOMEdit(nodeCA: C8Node): Promise<void> {
		const { cache } = this;

		this.attrUOMEnabled = false;
		this.attrUOMError = "";
		this.attrUOMTypeError = "";
		this.attrUOM = "";
		this.attrUOMName = "";
		this.attrUOMType = "";
		this.attrUOMTypeName = "";
		
		// try to load UOM node, the node which url is this CA url + UOM_SUFFIX
		// if the node exists, this mean UOM is enabled for this attribute and we will
		// load the node and initialize uom data for this form
		const uomUrl = getUOMConversionString(nodeCA.$URL);
		const uomNode = await resolveNode(uomUrl, cache);
		if (!uomNode) {
			return;
		}

		// find localized unit of measure and type name
		const conversionURL = JSON.parse(uomNode[ATTR_VALUE]);
		const { uomName, uomTypeName } = getUOMNameAndUOMTypeNameFromConversionCNL(conversionURL);
		const uomTypeUrl = generateUOMTypeURL(uomTypeName);
		const unitOfMeasureUrl = generateUnitOfMeasureURL(uomTypeName, uomName);

		await resolveNodesWithDependencies([unitOfMeasureUrl], [PATH_PARENT], cache);
		
		const unitOfMeasure = cache.get(unitOfMeasureUrl);
		const uomType = cache.get(uomTypeUrl);
		
		runInAction(()=> {
			this.attrUOMEnabled = true;
			this.attrUOM = unitOfMeasureUrl;
			this.attrUOMName = unitOfMeasure?.$Name;
			this.attrUOMType = uomTypeUrl;
			this.attrUOMTypeName = uomType?.$Name;
		});
	}

	/** find EnumList url if the attribute type is enum */
	private async _initDependsOn(dependsOnAttributeUrl?: cnl): Promise<void> {
		if (dependsOnAttributeUrl) {
			const attribute = await resolveNode(dependsOnAttributeUrl, this.cache);

			runInAction(() => {
				if (attribute) {
					this.dependsOnAttribute = createConfigurableAttributeSelectOption(attribute);
				}
			});
		}
	}

	/** find EnumList url if the attribute type is enum */
	private async _initEnum(): Promise<void> {
		const { attrSelectEnumeration } = this;

		if (attrSelectEnumeration) {
			// find url for selected enum if not already known
			const search = new Search().byType(TYPE_ENUM);
			search.getQuery().Node(xml.PROP_NAME, Operator.EQUALS, attrSelectEnumeration);
			const response = await search.execute();

			// TODO C8-74094: Try using datastore (T)EnumList(P)all with ad-hoc filter

			runInAction(() => {
				this.attrSelectEnumerationCnl = response.getResultNode()?.$URL;
				this._initEnumDependsOn();
			});
		}
	}

	/** find EnumList url if the attribute type is enum */
	private async _initEnumDependsOn(): Promise<void> {
		const { attrSelectEnumerationCnl, cache } = this;
		let dependsOn: C8Node;
		
		if (attrSelectEnumerationCnl) {
			dependsOn = await resolveNode(getUrlPath(attrSelectEnumerationCnl, pathCreate(ATTR_DEPENDS_ON)), cache);
		}

		runInAction(() => {
			this.dependsOnEnumList = dependsOn?.$Name;
		});
	};

	/** find url of LookupItem subtype on which the selected subtype depends */
	private async _initRef(subtypeUrl?: cnl): Promise<void> {
		const { cache, targetClass, targetClassSubtype } = this;
		let dependsOnSubtype: C8Node;
		let subtype: C8Node;

		if (subtypeUrl) {
			subtype = getNode(subtypeUrl) || await resolveNode(subtypeUrl);
		} else if (targetClassSubtype) {
			subtypeUrl = targetClassSubtype.id;
		}

		if ((targetClass === TYPE_LOOKUP_ITEM) && subtypeUrl) {
			dependsOnSubtype = await resolveNode(getUrlPath(subtypeUrl, pathCreate(ATTR_SETUP_SETTINGS, "DependsOnType")), cache);
		}

		runInAction(() => {
			this.dependsOnSubtypeUrl = dependsOnSubtype?.$URL;

			if (subtype) {
				this.targetClassSubtype = {
					id: subtypeUrl,
					name: subtype.$Name,
				};
			}
		});
	};

	/** validate the initial value */
	@action private _validateInitialValue = (): void => {
		const { attrFormat, attrInitialValue, attrType } = this;
		try {
			validateInitialValue(attrInitialValue, attrType, attrFormat);
			this.attrInitialValueError = "";
		} catch (ex) {
			this.attrInitialValueError = ex.message;
		}
	};

	constructor(nodeCA: C8Node, nodeBO: C8Node, cache: ICache, loc: ILocaleProvider) {
		super();

		this.nodeBO = nodeBO;
		this.cache = cache;
		this.loc = loc;
		this.locale = new AttributeDefinitionLocale(loc);
		
		if (nodeCA) {
			const isNewFrom = (nodeCA.__Parent__ !== nodeBO.$URL);

			if (isNewFrom) {
				// new from inherited case
				this.sourceNodeCA = nodeCA;
			} else {
				// edit case
				this.nodeCA = nodeCA;
			}

			this._initFromNode(nodeCA, isNewFrom);
		} else {
			this.resetStore();
		}
		
		makeObservable(this);
	}
}

export const AttributeDefinitionStoreContext = createContext<AttributeDefinitionStore>(undefined);
export const useAttributeDefinitionStore = (): AttributeDefinitionStore => useContext(AttributeDefinitionStoreContext);
